import React, { useState } from 'react';
import { AlertCircle, X, ChevronDown, ChevronUp, Copy, Download } from 'lucide-react';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { GraphConfig, GraphData, DiffsAnalyticsResponse, EventCategoryFilter, DiffsBreakdownType, DiffsCrosstabResponse, DiffsCrosstabRequest } from './types';
import { BREAKDOWN_METADATA, HISTORICAL_METRIC_METADATA, DIFFS_BREAKDOWN_METADATA, EVENT_TYPE_CATEGORIES, EVENT_CATEGORY_METADATA, shiftQuarterBack } from './types';
import type { HistoricalAnalyticsResponse } from '@/utils/historicalAnalyticsAPI';
import { useAnalyticsCompanies } from '@/hooks/useAnalyticsCompanies';
import { useDiffsCrosstab } from '@/hooks/useDiffsCrosstab';
import DiffsDrilldownModal, { TableOption } from './DiffsDrilldownModal';
import type { ExampleConfig, ExampleTableRow } from '@/components/reports/example/types';

interface GraphRendererProps {
  config: GraphConfig;
  data?: GraphData | HistoricalAnalyticsResponse | DiffsAnalyticsResponse;  // Can be any type
  isLoading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onConfigChange?: (config: GraphConfig) => void;
  onCreateExample?: (config: ExampleConfig) => void;
  onAddToTable?: (row: Omit<ExampleTableRow, 'id'>, tableId?: string) => void;
  availableTables?: TableOption[];
  lastSelectedTableId?: string | null;
  onTableSelected?: (tableId: string | null) => void;
}

export const GraphRenderer: React.FC<GraphRendererProps> = ({
  config,
  data,
  isLoading,
  error,
  onRefresh,
  onConfigChange,
  onCreateExample,
  onAddToTable,
  availableTables,
  lastSelectedTableId,
  onTableSelected,
}) => {
  // Detect mode first (before any conditional returns)
  const isHistoricalMode = config.mode === 'historical';
  const isDiffsMode = config.mode === 'diffs';

  // Modal state for showing companies when clicking a bar (always declare hooks)
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [accumulatedCompanies, setAccumulatedCompanies] = useState<any[]>([]);

  // Data panel state for diffs mode
  const [dataExpanded, setDataExpanded] = useState(false);

  // Drilldown modal state for diffs mode
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownPeriod, setDrilldownPeriod] = useState<string>('');
  const [drilldownBreakdownType, setDrilldownBreakdownType] = useState<DiffsBreakdownType>('event_type');
  const [drilldownBreakdownValue, setDrilldownBreakdownValue] = useState<string>('');

  // Fetch companies for selected period (conditional based on mode and selection)
  const companiesRequest = isHistoricalMode && selectedPeriod ? {
    period: selectedPeriod,
    filters: { has_addons: true },
    sort: { field: 'total_addons', order: 'desc' as const }
  } : null;

  const { data: companiesData, isLoading: isLoadingCompanies } = useAnalyticsCompanies(companiesRequest, offset);

  // Crosstab request for categories chart mode
  const crosstabRequest: DiffsCrosstabRequest | null = isDiffsMode && config.diffsChartMode === 'categories' && config.diffsSelectedPeriods?.length
    ? {
        periods: config.diffsSelectedPeriods,
        group_by: 'category',
        options: { limit: 20 }
      }
    : null;

  const { data: crosstabData, isLoading: isLoadingCrosstab, error: crosstabError } = useDiffsCrosstab(crosstabRequest);

  // Accumulate results when new data arrives
  React.useEffect(() => {
    if (companiesData?.results) {
      if (offset === 0) {
        // First load - replace all results
        setAccumulatedCompanies(companiesData.results);
      } else {
        // Load more - append new results
        setAccumulatedCompanies(prev => [...prev, ...companiesData.results]);
      }
    }
  }, [companiesData, offset]);

  const handleBarClick = (data: any) => {
    if (data && data.period) {
      setSelectedPeriod(data.period);
      setOffset(0);
      setAccumulatedCompanies([]);
      setModalOpen(true);
    }
  };

  const handleLoadMore = () => {
    setOffset(o => o + 100);
  };

  // Handler for opening drilldown modal (diffs mode)
  const handleDrilldown = (period: string, breakdownType: DiffsBreakdownType, breakdownValue: string) => {
    setDrilldownPeriod(period);
    setDrilldownBreakdownType(breakdownType);
    setDrilldownBreakdownValue(breakdownValue);
    setDrilldownOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-[300px] bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-900">Failed to load analytics</h4>
            <p className="text-sm text-red-700 mt-1">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  // Historical Mode Rendering
  if (isHistoricalMode) {
    const historicalData = data as HistoricalAnalyticsResponse;

    if (!historicalData.overall || Object.keys(historicalData.overall).length === 0) {
      return (
        <div className="w-full text-center py-8 text-gray-500">
          <p>No overlapping companies found for selected periods</p>
        </div>
      );
    }

    const metricMeta = HISTORICAL_METRIC_METADATA[config.metric || 'average_addons'];
    const title = config.title || metricMeta.label;

    // Transform historical data for bar chart
    const chartData = historicalData.periods
      .map(period => ({
        period,
        value: historicalData.overall[period]?.avg || 0,
        count: historicalData.overall[period]?.count || 0,
      }))
      .sort((a, b) => a.period.localeCompare(b.period)); // Chronological order

    return (
      <div className="w-full space-y-2">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">
            {metricMeta.description} • {historicalData.metadata.overlapping_companies} overlapping companies
          </p>
        </div>

        {/* Bar Chart */}
        <div className="h-[500px]">
          <ChartContainer config={{}} className="h-full w-full">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
            >
              <XAxis
                dataKey="period"
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{ value: 'Time Period', position: 'insideBottom', offset: -5, fill: '#000000' }}
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{
                  value: metricMeta.yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#000000',
                }}
              />
              <Bar
                dataKey="value"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                onClick={handleBarClick}
                cursor="pointer"
              />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                        <p className="font-medium">{payload[0].payload.period}</p>
                        <p className="text-sm">
                          {metricMeta.yAxisLabel}: <strong>{payload[0].value?.toFixed(2)}</strong>
                        </p>
                        <p className="text-xs text-gray-500">
                          Based on {payload[0].payload.count} companies
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Click to view companies
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </BarChart>
          </ChartContainer>
        </div>

        {/* Modal for showing companies */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>
                Companies with Add-ons - {selectedPeriod}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {isLoadingCompanies && offset === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : accumulatedCompanies.length > 0 ? (
                <div className="space-y-2">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pages</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Add-ons</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plans</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {accumulatedCompanies.map((result: any) => (
                        <tr key={result.company.slug} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {result.company.logo_url && (
                                <img src={result.company.logo_url} alt="" className="w-6 h-6 rounded" />
                              )}
                              <a
                                href={`/edit/${result.company.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline font-medium"
                              >
                                {result.company.name}
                              </a>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{result.metrics.page_count || 0}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{result.metrics.total_addons || 0}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{result.metrics.total_plans || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {companiesData?.pagination?.has_more && (
                    <div className="flex justify-center py-4">
                      <Button
                        onClick={handleLoadMore}
                        variant="outline"
                        disabled={isLoadingCompanies}
                      >
                        {isLoadingCompanies ? 'Loading...' : 'Load More'}
                      </Button>
                    </div>
                  )}
                  <div className="text-xs text-gray-500 text-center py-2">
                    Showing {accumulatedCompanies.length} of {companiesData?.pagination?.total || 0} companies
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No companies found with add-ons for this period
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Helper function to aggregate event counts by category
  const aggregateEventCountsByCategory = (
    eventCounts: Record<string, number>,
    eventTypes: string[]
  ): Record<string, number> => {
    const categoryTotals = { pricing: 0, packaging: 0, product: 0 };
    eventTypes.forEach(eventType => {
      const category = EVENT_TYPE_CATEGORIES[eventType];
      if (category && category !== 'all') {
        categoryTotals[category as keyof typeof categoryTotals] += eventCounts[eventType] || 0;
      }
    });
    return categoryTotals;
  };

  // Diffs Mode - Event Categories Chart (event_category breakdown with categories on X-axis)
  if (isDiffsMode && config.diffsBreakdown === 'event_category' && config.diffsChartMode === 'categories') {
    const diffsData = data as DiffsAnalyticsResponse;

    if (!diffsData.by_event_category || Object.keys(diffsData.by_event_category).length === 0) {
      return (
        <div className="w-full text-center py-8 text-gray-500">
          <p>No event category data found</p>
        </div>
      );
    }

    const title = config.title || 'Events by Category';

    // Transform data for grouped bar chart
    // X-axis: event categories (pricing, packaging, product)
    // Grouped bars: one for each period (2024, 2025, etc.)
    const eventCategories = ['pricing', 'packaging', 'product'];
    const periods = diffsData.periods || [];

    const chartData = eventCategories.map(category => {
      const base: Record<string, any> = { category };

      // Add count for each period
      periods.forEach(period => {
        const count = diffsData.by_event_category?.[category]?.[period]?.count || 0;
        base[period] = count;
      });

      return base;
    });

    // Calculate grand total
    const grandTotal = chartData.reduce((sum, d) => {
      const categoryTotal = periods.reduce((pSum, p) => pSum + (d[p] || 0), 0);
      return sum + categoryTotal;
    }, 0);

    // Period colors
    const PERIOD_COLORS = [
      '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6',
      '#06b6d4', '#ef4444', '#6366f1', '#14b8a6', '#f97316',
    ];

    return (
      <div className="w-full space-y-2">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
            {grandTotal > 0 && (
              <span className="ml-2 text-base font-normal text-gray-600">
                ({grandTotal.toLocaleString()} total events)
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-500">
            Event categories across {periods.length} period{periods.length !== 1 ? 's' : ''} • Grouped by period
          </p>
        </div>

        {/* Grouped Bar Chart */}
        <div className="h-[500px]">
          <ChartContainer config={{}} className="h-full w-full">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 60, bottom: 80 }}
            >
              <XAxis
                dataKey="category"
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                tickFormatter={(value) => {
                  const formatted = value.charAt(0).toUpperCase() + value.slice(1);
                  return formatted;
                }}
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{
                  value: 'Event Count',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#000000',
                }}
              />
              {periods.map((period, index) => (
                <Bar
                  key={period}
                  dataKey={period}
                  fill={PERIOD_COLORS[index % PERIOD_COLORS.length]}
                  name={period.replace('Y', '')}
                />
              ))}
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => value}
              />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;

                  const dataPoint = payload[0].payload;
                  const category = dataPoint.category;

                  return (
                    <div className="bg-white p-3 border border-gray-200 shadow-lg rounded">
                      <div className="font-medium mb-2 capitalize">{category} Events</div>
                      {payload.map((entry: any) => (
                        <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
                          <div
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: entry.fill }}
                          />
                          <span>{entry.name}:</span>
                          <span className="font-medium">{entry.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
            </BarChart>
          </ChartContainer>
        </div>
      </div>
    );
  }

  // Diffs Mode - Categories Chart (crosstab for company categories)
  if (isDiffsMode && config.diffsChartMode === 'categories') {
    // Show loading state for crosstab
    if (isLoadingCrosstab) {
      return (
        <div className="w-full">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="h-[300px] bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    // Show error for crosstab
    if (crosstabError) {
      return (
        <div className="w-full">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-900">Failed to load category analytics</h4>
              <p className="text-sm text-red-700 mt-1">{crosstabError.message}</p>
            </div>
          </div>
        </div>
      );
    }

    // No crosstab data
    if (!crosstabData || !crosstabData.data || crosstabData.data.length === 0) {
      return (
        <div className="w-full text-center py-8 text-gray-500">
          <p>No category data found for selected periods</p>
        </div>
      );
    }

    const title = config.title || 'Events by Category';

    // Color palette for stacked bars (event types)
    const EVENT_TYPE_COLORS: Record<string, string> = {
      // Pricing events - green shades
      price_increased: '#059669',
      price_decreased: '#10b981',
      price_hidden: '#34d399',
      price_revealed: '#6ee7b7',
      discount_added: '#047857',
      discount_removed: '#065f46',
      discount_changed: '#064e3b',
      pricing_restructured: '#0d9488',
      // Plans events - blue/purple shades
      plan_added: '#3b82f6',
      plan_removed: '#2563eb',
      plan_renamed: '#1d4ed8',
      pricing_metric_changed: '#6366f1',
      threshold_added: '#4f46e5',
      threshold_removed: '#4338ca',
      capacity_increased: '#7c3aed',
      capacity_decreased: '#6d28d9',
      trial_changed: '#8b5cf6',
      freemium_changed: '#a78bfa',
      // Add-ons - orange/yellow shades
      addon_added: '#f59e0b',
      addon_removed: '#d97706',
      addon_changed: '#b45309',
      // Features - pink/red shades
      feature_added: '#ec4899',
      feature_removed: '#db2777',
      feature_changed: '#be185d',
    };

    // All event types in order
    const ALL_EVENT_TYPES = [
      'price_increased', 'price_decreased', 'price_hidden', 'price_revealed',
      'discount_added', 'discount_removed', 'discount_changed', 'pricing_restructured',
      'plan_added', 'plan_removed', 'plan_renamed', 'pricing_metric_changed',
      'threshold_added', 'threshold_removed', 'capacity_increased', 'capacity_decreased',
      'trial_changed', 'freemium_changed',
      'addon_added', 'addon_removed', 'addon_changed',
      'feature_added', 'feature_removed', 'feature_changed',
    ];

    // Filter event types based on selected category (pricing/packaging/product)
    const eventCategoryFilter = config.diffsEventCategory || 'all';
    const filteredEventTypes = eventCategoryFilter === 'all'
      ? ALL_EVENT_TYPES
      : ALL_EVENT_TYPES.filter(et => EVENT_TYPE_CATEGORIES[et] === eventCategoryFilter);

    // Transform crosstab data for stacked bar chart
    // X-axis: categories, Y-axis: event counts stacked by event type or category
    const chartData = crosstabData.data.map(item => {
      const categoryName = item.category_name ?? item.category;
      const base: Record<string, any> = {
        category: categoryName,
        category_id: item.category_id ?? item.category,
      };

      if (config.diffsGroupByCategory) {
        // Group by high-level category (pricing/packaging/product)
        const categoryTotals = aggregateEventCountsByCategory(item.event_counts, filteredEventTypes);
        base.pricing = categoryTotals.pricing;
        base.packaging = categoryTotals.packaging;
        base.product = categoryTotals.product;
        base.total = categoryTotals.pricing + categoryTotals.packaging + categoryTotals.product;
      } else {
        // Individual event types
        let filteredTotal = 0;
        filteredEventTypes.forEach(eventType => {
          const count = item.event_counts[eventType as keyof typeof item.event_counts] || 0;
          base[eventType] = count;
          filteredTotal += count;
        });
        base.total = filteredTotal;
      }

      return base;
    }).filter(d => d.total > 0) // Only show categories with data after filtering
      .sort((a, b) => b.total - a.total); // Sort by total descending

    // Determine stack keys based on grouping mode
    const stackKeys = config.diffsGroupByCategory
      ? ['pricing', 'packaging', 'product'].filter(cat =>
          chartData.some(d => d[cat] > 0)
        )
      : filteredEventTypes.filter(et =>
          chartData.some(d => d[et] > 0)
        );

    // Calculate grand total (from filtered data)
    const grandTotal = chartData.reduce((sum, d) => sum + d.total, 0);

    return (
      <div className="w-full space-y-2">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
            {grandTotal > 0 && (
              <span className="ml-2 text-base font-normal text-gray-600">
                ({grandTotal.toLocaleString()} total events)
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-500">
            {eventCategoryFilter !== 'all'
              ? `${EVENT_CATEGORY_METADATA[eventCategoryFilter].label} by company category`
              : 'Events by company category'
            } across {crosstabData.periods.length} periods • Stacked by event type
          </p>
        </div>

        {/* Stacked Bar Chart */}
        <div className="h-[500px]">
          <ChartContainer config={{}} className="h-full w-full">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 60, bottom: 120 }}
              layout="horizontal"
            >
              <XAxis
                dataKey="category"
                tick={{ fill: '#000000', fontSize: 10 }}
                stroke="#000000"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={100}
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{
                  value: 'Event Count',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#000000',
                }}
              />
              {stackKeys.map(key => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="events"
                  fill={
                    config.diffsGroupByCategory
                      ? EVENT_CATEGORY_METADATA[key as EventCategoryFilter]?.color || '#94a3b8'
                      : EVENT_TYPE_COLORS[key] || '#94a3b8'
                  }
                  name={
                    config.diffsGroupByCategory
                      ? EVENT_CATEGORY_METADATA[key as EventCategoryFilter]?.label || key
                      : key.replace(/_/g, ' ')
                  }
                />
              ))}
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => {
                  if (config.diffsGroupByCategory) {
                    return value; // Already formatted as "Pricing Events", etc.
                  }
                  return String(value).replace(/_/g, ' ');
                }}
              />
              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    const categoryData = chartData.find(d => d.category === label);
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs">
                        <p className="font-medium border-b pb-1 mb-2">{label}</p>
                        <p className="text-xs text-gray-600 mb-2">
                          Total: {categoryData?.total.toLocaleString() || 0} events
                        </p>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {payload
                            .filter(p => (p.value as number) > 0)
                            .sort((a, b) => (b.value as number) - (a.value as number))
                            .map((entry, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                                <span className="flex items-center gap-1">
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-gray-700">
                                    {String(entry.name).replace(/_/g, ' ')}
                                  </span>
                                </span>
                                <span className="font-medium">{entry.value}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </BarChart>
          </ChartContainer>
        </div>

        {/* Stats Footer */}
        <div className="flex flex-wrap gap-4 pt-2 border-t text-sm text-gray-600">
          <div>
            <span className="font-medium">{crosstabData.totals?.categories || chartData.length}</span>
            <span className="ml-1">categories</span>
          </div>
          <div>
            <span className="font-medium">{grandTotal.toLocaleString()}</span>
            <span className="ml-1">total events</span>
          </div>
          <div>
            <span className="font-medium">{crosstabData.totals?.companies || 0}</span>
            <span className="ml-1">companies</span>
          </div>
          <div>
            <span className="font-medium">{crosstabData.periods.length}</span>
            <span className="ml-1">periods analyzed</span>
          </div>
        </div>

        {/* Expandable Data Table */}
        <div className="mt-4 border rounded-lg">
          <button
            onClick={() => setDataExpanded(!dataExpanded)}
            className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span>View Data Table</span>
            {dataExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {dataExpanded && (
            <div className="border-t p-3">
              {/* Export buttons */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => {
                    const headers = [
                      'Category',
                      ...stackKeys.map(k =>
                        config.diffsGroupByCategory
                          ? EVENT_CATEGORY_METADATA[k as EventCategoryFilter]?.label || k
                          : k.replace(/_/g, ' ')
                      ),
                      'Total'
                    ];
                    const rows = chartData.map(d => [
                      d.category,
                      ...stackKeys.map(k => d[k] || 0),
                      d.total
                    ]);
                    const tsv = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
                    navigator.clipboard.writeText(tsv);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  <Copy className="h-3 w-3" />
                  Copy TSV
                </button>
                <button
                  onClick={() => {
                    const headers = [
                      'Category',
                      ...stackKeys.map(k =>
                        config.diffsGroupByCategory
                          ? EVENT_CATEGORY_METADATA[k as EventCategoryFilter]?.label || k
                          : k.replace(/_/g, ' ')
                      ),
                      'Total'
                    ];
                    const rows = chartData.map(d => [
                      `"${d.category}"`,
                      ...stackKeys.map(k => d[k] || 0),
                      d.total
                    ]);
                    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `events-by-category-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  <Download className="h-3 w-3" />
                  Export CSV
                </button>
              </div>

              {/* Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium text-gray-700 sticky left-0 bg-gray-50">Category</th>
                      {stackKeys.map(key => (
                        <th key={key} className="px-3 py-2 text-right font-medium text-gray-700 whitespace-nowrap">
                          {config.diffsGroupByCategory
                            ? EVENT_CATEGORY_METADATA[key as EventCategoryFilter]?.label || key
                            : key.replace(/_/g, ' ')}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((row, idx) => (
                      <tr key={row.category_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 text-gray-900 sticky left-0 bg-inherit">{row.category}</td>
                        {stackKeys.map(key => (
                          <td key={key} className="px-3 py-2 text-right text-gray-700">
                            {row[key] || 0}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right font-medium text-gray-900">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Diffs Mode - Events Chart (event types on X-axis, periods grouped)
  if (isDiffsMode && config.diffsChartMode === 'events') {
    const diffsData = data as DiffsAnalyticsResponse;

    if (!diffsData.overall || Object.keys(diffsData.overall).length === 0) {
      return (
        <div className="w-full text-center py-8 text-gray-500">
          <p>No diff data found for selected periods</p>
        </div>
      );
    }

    const eventCategoryMeta = config.diffsEventCategory && config.diffsEventCategory !== 'all'
      ? EVENT_CATEGORY_METADATA[config.diffsEventCategory]
      : null;
    const title = config.title || (eventCategoryMeta ? eventCategoryMeta.label : 'Pricing Events by Type');

    // Get event type breakdown data
    const breakdownData = diffsData.by_event_type || {};

    // Get all event types
    let eventTypes = Object.keys(breakdownData);

    // Filter by event category if applicable
    const eventCategoryFilter = config.diffsEventCategory || 'all';
    if (eventCategoryFilter !== 'all') {
      eventTypes = eventTypes.filter(key => EVENT_TYPE_CATEGORIES[key] === eventCategoryFilter);
    }

    // Check if quarters should be shifted
    const shouldShiftQuarters = config.diffsShiftQuarters !== false;

    // Period colors for grouped bars
    const PERIOD_COLORS = [
      '#6366f1', // indigo
      '#8b5cf6', // violet
      '#a855f7', // purple
      '#d946ef', // fuchsia
      '#ec4899', // pink
      '#f43f5e', // rose
      '#3b82f6', // blue
      '#0ea5e9', // sky
    ];

    // Get sorted periods (chronological)
    const sortedPeriods = [...diffsData.periods].sort((a, b) => a.localeCompare(b));

    // Transform data: pivot from periods → event types to event types → periods
    const eventsChartData = eventTypes.map(eventType => {
      const row: Record<string, any> = {
        eventType,
        eventTypeDisplay: eventType.replace(/_/g, ' '),
        _total: 0
      };

      sortedPeriods.forEach(period => {
        const displayPeriod = shouldShiftQuarters ? shiftQuarterBack(period) : period;
        const count = breakdownData[eventType]?.[period]?.count || 0;
        row[period] = count;
        row[`${period}_display`] = displayPeriod;
        row._total += count;
      });

      return row;
    })
    .filter(row => row._total > 0) // Only show event types with data
    .sort((a, b) => b._total - a._total); // Sort by total descending

    // Calculate grand total
    const grandTotal = eventsChartData.reduce((sum, row) => sum + row._total, 0);

    // Format period for display (remove Y suffix for yearly, shift quarters if needed)
    const formatPeriodDisplay = (period: string) => {
      if (period.endsWith('Y')) {
        return period.replace('Y', '');
      }
      return shouldShiftQuarters ? shiftQuarterBack(period) : period;
    };

    return (
      <div className="w-full space-y-2">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
            {grandTotal > 0 && (
              <span className="ml-2 text-base font-normal text-gray-600">
                ({grandTotal.toLocaleString()} total events)
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-500">
            Event types grouped by period • {sortedPeriods.length} periods
          </p>
        </div>

        {/* Grouped Bar Chart */}
        <div className="h-[500px]">
          <ChartContainer config={{}} className="h-full w-full">
            <BarChart
              data={eventsChartData}
              margin={{ top: 20, right: 30, left: 60, bottom: 120 }}
              layout="horizontal"
            >
              <XAxis
                dataKey="eventTypeDisplay"
                tick={{ fill: '#000000', fontSize: 10 }}
                stroke="#000000"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={100}
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{
                  value: 'Event Count',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#000000',
                }}
              />
              {sortedPeriods.map((period, index) => (
                <Bar
                  key={period}
                  dataKey={period}
                  name={formatPeriodDisplay(period)}
                  fill={PERIOD_COLORS[index % PERIOD_COLORS.length]}
                />
              ))}
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
              />
              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    const eventData = eventsChartData.find(d => d.eventTypeDisplay === label);
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs">
                        <p className="font-medium border-b pb-1 mb-2">{label}</p>
                        <p className="text-xs text-gray-600 mb-2">
                          Total: {eventData?._total.toLocaleString() || 0} events
                        </p>
                        <div className="space-y-1">
                          {payload
                            .filter(p => (p.value as number) > 0)
                            .sort((a, b) => (b.value as number) - (a.value as number))
                            .map((entry, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                                <span className="flex items-center gap-1">
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-gray-700">{entry.name}</span>
                                </span>
                                <span className="font-medium">{entry.value}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </BarChart>
          </ChartContainer>
        </div>

        {/* Stats Footer */}
        <div className="flex flex-wrap gap-4 pt-2 border-t text-sm text-gray-600">
          <div>
            <span className="font-medium">{eventsChartData.length}</span>
            <span className="ml-1">event types</span>
          </div>
          <div>
            <span className="font-medium">{grandTotal.toLocaleString()}</span>
            <span className="ml-1">total events</span>
          </div>
          <div>
            <span className="font-medium">{sortedPeriods.length}</span>
            <span className="ml-1">periods</span>
          </div>
        </div>

        {/* Expandable Data Table */}
        <div className="mt-4 border rounded-lg">
          <button
            onClick={() => setDataExpanded(!dataExpanded)}
            className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span>View Data Table</span>
            {dataExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {dataExpanded && (
            <div className="border-t p-3">
              {/* Export buttons */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => {
                    const headers = ['Event Type', ...sortedPeriods.map(formatPeriodDisplay), 'Total'];
                    const rows = eventsChartData.map(d => [
                      d.eventTypeDisplay,
                      ...sortedPeriods.map(p => d[p] || 0),
                      d._total
                    ]);
                    const tsv = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
                    navigator.clipboard.writeText(tsv);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  <Copy className="h-3 w-3" />
                  Copy TSV
                </button>
                <button
                  onClick={() => {
                    const headers = ['Event Type', ...sortedPeriods.map(formatPeriodDisplay), 'Total'];
                    const rows = eventsChartData.map(d => [
                      `"${d.eventTypeDisplay}"`,
                      ...sortedPeriods.map(p => d[p] || 0),
                      d._total
                    ]);
                    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `events-by-type-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  <Download className="h-3 w-3" />
                  Export CSV
                </button>
              </div>

              {/* Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium text-gray-700 sticky left-0 bg-gray-50">Event Type</th>
                      {sortedPeriods.map(period => (
                        <th key={period} className="px-3 py-2 text-right font-medium text-gray-700">
                          {formatPeriodDisplay(period)}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsChartData.map((row, idx) => (
                      <tr key={row.eventType} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 text-gray-900 sticky left-0 bg-inherit">{row.eventTypeDisplay}</td>
                        {sortedPeriods.map(period => (
                          <td key={period} className="px-3 py-2 text-right text-gray-700">
                            {row[period] || 0}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right font-medium text-gray-900">{row._total}</td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr className="bg-gray-100 font-medium">
                      <td className="px-3 py-2 text-gray-900 sticky left-0 bg-gray-100">TOTAL</td>
                      {sortedPeriods.map(period => (
                        <td key={period} className="px-3 py-2 text-right text-gray-900">
                          {eventsChartData.reduce((sum, d) => sum + (d[period] || 0), 0)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right text-gray-900">{grandTotal}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Diffs Mode Rendering
  if (isDiffsMode) {
    const diffsData = data as DiffsAnalyticsResponse;

    if (!diffsData.overall || Object.keys(diffsData.overall).length === 0) {
      return (
        <div className="w-full text-center py-8 text-gray-500">
          <p>No diff data found for selected periods</p>
        </div>
      );
    }

    const breakdownMeta = DIFFS_BREAKDOWN_METADATA[config.diffsBreakdown || 'event_type'];
    const eventCategoryMeta = config.diffsBreakdown === 'event_type' && config.diffsEventCategory && config.diffsEventCategory !== 'all'
      ? EVENT_CATEGORY_METADATA[config.diffsEventCategory]
      : null;
    const title = config.title || (eventCategoryMeta ? eventCategoryMeta.label : breakdownMeta.label);

    // Get the appropriate breakdown data based on config
    const breakdownKey = config.diffsBreakdown === 'category' ? 'by_category'
      : config.diffsBreakdown === 'employees' ? 'by_employees'
      : config.diffsBreakdown === 'event_category' ? 'by_event_category'
      : 'by_event_type';

    const breakdownData = diffsData[breakdownKey] || {};

    // Get unique breakdown keys (e.g., event types, categories)
    let breakdownKeys = Object.keys(breakdownData);

    // Filter event types by category if applicable
    const eventCategoryFilter = config.diffsEventCategory || 'all';
    if (config.diffsBreakdown === 'event_type' && eventCategoryFilter !== 'all') {
      breakdownKeys = breakdownKeys.filter(key => EVENT_TYPE_CATEGORIES[key] === eventCategoryFilter);
    }

    // Color palette for stacked bars
    const STACK_COLORS = [
      '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
      '#06b6d4', '#f97316', '#a855f7', '#14b8a6', '#6366f1',
      '#ef4444', '#84cc16', '#22c55e', '#0ea5e9', '#d946ef',
      '#fbbf24', '#34d399', '#60a5fa', '#c084fc', '#fb7185',
      '#4ade80', '#2dd4bf', '#a3e635', '#facc15',
    ];

    // Check if quarters should be shifted
    const shouldShiftQuarters = config.diffsShiftQuarters !== false; // Default true

    // Transform data for bar chart
    const chartData = diffsData.periods
      .map(period => {
        const displayPeriod = shouldShiftQuarters ? shiftQuarterBack(period) : period;
        const base: Record<string, any> = { period, displayPeriod };

        // Add overall metrics
        const overall = diffsData.overall[period];
        base._total_events = overall?.total_events || 0;
        base._unique_companies = overall?.unique_companies || 0;

        // Add each breakdown value and calculate filtered total
        let filteredTotal = 0;
        breakdownKeys.forEach(key => {
          const count = breakdownData[key]?.[period]?.count || 0;
          base[key] = count;
          filteredTotal += count;
        });
        base._filtered_total = filteredTotal;

        return base;
      })
      .sort((a, b) => a.period.localeCompare(b.period)); // Chronological order

    // Calculate totals per breakdown key for legend ordering
    const breakdownTotals = breakdownKeys.map(key => ({
      key,
      total: chartData.reduce((sum, d) => sum + (d[key] || 0), 0)
    })).sort((a, b) => b.total - a.total);

    // Only show top 15 breakdown keys to avoid cluttering
    const topBreakdownKeys = breakdownTotals.slice(0, 15).map(b => b.key);

    // Calculate grand total for header (sum filtered breakdown keys, not _total_events)
    const grandTotal = breakdownTotals.reduce((sum, b) => sum + b.total, 0);

    const countMode = config.diffsCountMode || 'unique';

    const handleCountModeToggle = () => {
      if (onConfigChange) {
        onConfigChange({
          ...config,
          diffsCountMode: countMode === 'unique' ? 'all' : 'unique'
        });
      }
    };

    return (
      <div className="w-full space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
              {grandTotal > 0 && (
                <span className="ml-2 text-base font-normal text-gray-600">
                  ({grandTotal.toLocaleString()} total {countMode === 'unique' ? 'pages' : 'events'})
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-500">
              {breakdownMeta.description}
            </p>
          </div>
          {onConfigChange && (
            <div className="flex items-center gap-2 text-sm">
              <span className={countMode === 'all' ? 'text-gray-900 font-medium' : 'text-gray-400'}>All</span>
              <button
                type="button"
                onClick={handleCountModeToggle}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  countMode === 'unique' ? 'bg-purple-600' : 'bg-gray-300'
                }`}
                title={countMode === 'unique' ? 'Counting unique pages per period' : 'Counting all events'}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    countMode === 'unique' ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={countMode === 'unique' ? 'text-gray-900 font-medium' : 'text-gray-400'}>Unique</span>
            </div>
          )}
        </div>

        {/* Bar Chart (stacked or grouped with total line) */}
        <div className="h-[500px]">
          <ChartContainer config={{}} className="h-full w-full">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
            >
              <XAxis
                dataKey="displayPeriod"
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{ value: 'Time Period', position: 'insideBottom', offset: -5, fill: '#000000' }}
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{
                  value: 'Event Count',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#000000',
                }}
              />
              {topBreakdownKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  {...(config.chartType === 'bar' ? { stackId: 'a' } : {})}
                  fill={STACK_COLORS[index % STACK_COLORS.length]}
                  name={key.replace(/_/g, ' ')}
                />
              ))}
              {config.chartType === 'grouped' && config.diffsShowTrendLine !== false && (
                <Line
                  type="monotone"
                  dataKey="_filtered_total"
                  stroke="#1f2937"
                  strokeWidth={2}
                  dot={{ fill: '#1f2937', strokeWidth: 2, r: 4 }}
                  name="Total"
                />
              )}
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => value === '_filtered_total' ? 'Total' : value.replace(/_/g, ' ')}
              />
              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    const periodData = chartData.find(d => d.displayPeriod === label);
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs">
                        <p className="font-medium border-b pb-1 mb-2">{label}</p>
                        <p className="text-xs text-gray-600 mb-2">
                          Total: {periodData?._filtered_total || 0} events •{' '}
                          {periodData?._unique_companies || 0} companies
                        </p>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {payload
                            .filter(p => (p.value as number) > 0)
                            .sort((a, b) => (b.value as number) - (a.value as number))
                            .map((entry, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                                <span className="flex items-center gap-1">
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-gray-700">
                                    {String(entry.name).replace(/_/g, ' ')}
                                  </span>
                                </span>
                                <span className="font-medium">{entry.value}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </ComposedChart>
          </ChartContainer>
        </div>

        {/* Stats Footer */}
        <div className="flex flex-wrap gap-4 pt-2 border-t text-sm text-gray-600">
          {diffsData.metadata.pages_with_all_periods !== undefined && (
            <div>
              <span className="font-medium">{diffsData.metadata.pages_with_all_periods.toLocaleString()}</span>
              <span className="ml-1">pages matched</span>
            </div>
          )}
          {diffsData.metadata.pages_excluded !== undefined && diffsData.metadata.pages_excluded > 0 && (
            <div>
              <span className="font-medium">{diffsData.metadata.pages_excluded.toLocaleString()}</span>
              <span className="ml-1">pages excluded</span>
            </div>
          )}
          <div>
            <span className="font-medium">{grandTotal.toLocaleString()}</span>
            <span className="ml-1">total events</span>
          </div>
          {diffsData.options?.require_all_periods && (
            <div className="text-purple-600">
              Filtered to pages with all {diffsData.periods.length} periods
            </div>
          )}
        </div>

        {/* Expandable Data Panel */}
        <div className="mt-4 border rounded-lg">
          <button
            onClick={() => setDataExpanded(!dataExpanded)}
            className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span>View Data Table</span>
            {dataExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {dataExpanded && (
            <div className="border-t p-3">
              {/* Export buttons */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => {
                    const displayPeriods = chartData.map(d => d.displayPeriod);
                    const headers = ['Event Type', ...displayPeriods, 'Total'];
                    const rows = topBreakdownKeys.map(key => {
                      const rowTotal = chartData.reduce((sum, d) => sum + (d[key] || 0), 0);
                      return [key.replace(/_/g, ' '), ...chartData.map(d => d[key] || 0), rowTotal];
                    });
                    // Add totals row
                    const totalsRow = ['TOTAL', ...chartData.map(d => d._filtered_total || 0), grandTotal];
                    rows.push(totalsRow);

                    const tsv = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
                    navigator.clipboard.writeText(tsv);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  <Copy className="h-3 w-3" />
                  Copy to Clipboard
                </button>
                <button
                  onClick={() => {
                    const displayPeriods = chartData.map(d => d.displayPeriod);
                    const headers = ['Event Type', ...displayPeriods, 'Total'];
                    const rows = topBreakdownKeys.map(key => {
                      const rowTotal = chartData.reduce((sum, d) => sum + (d[key] || 0), 0);
                      return [key.replace(/_/g, ' '), ...chartData.map(d => d[key] || 0), rowTotal];
                    });
                    const totalsRow = ['TOTAL', ...chartData.map(d => d._filtered_total || 0), grandTotal];
                    rows.push(totalsRow);

                    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `diffs-data-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  <Download className="h-3 w-3" />
                  Export CSV
                </button>
              </div>

              {/* Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Event Type</th>
                      {chartData.map(d => (
                        <th key={d.period} className="px-3 py-2 text-right font-medium text-gray-700">{d.displayPeriod}</th>
                      ))}
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topBreakdownKeys.map((key, idx) => {
                      const rowTotal = chartData.reduce((sum, d) => sum + (d[key] || 0), 0);
                      return (
                        <tr key={key} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 text-gray-900">{key.replace(/_/g, ' ')}</td>
                          {chartData.map(d => (
                            <td
                              key={d.period}
                              className="px-3 py-2 text-right text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                              onClick={() => d[key] > 0 && handleDrilldown(d.period, config.diffsBreakdown || 'event_type', key)}
                              title={d[key] > 0 ? 'Click to see details' : undefined}
                            >
                              {d[key] || 0}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right font-medium text-gray-900">{rowTotal}</td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr className="bg-gray-100 font-medium">
                      <td className="px-3 py-2 text-gray-900">TOTAL</td>
                      {chartData.map(d => (
                        <td key={d.period} className="px-3 py-2 text-right text-gray-900">
                          {d._filtered_total || 0}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right text-gray-900">{grandTotal}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Drilldown Modal */}
        <DiffsDrilldownModal
          isOpen={drilldownOpen}
          onOpenChange={setDrilldownOpen}
          period={drilldownPeriod}
          breakdownType={drilldownBreakdownType}
          breakdownValue={drilldownBreakdownValue}
          shiftQuarters={config.diffsShiftQuarters !== false}
          allPeriods={config.diffsSelectedPeriods}
          requireAllPeriods={config.diffsRequireAllPeriods}
          countMode={config.diffsCountMode || 'unique'}
          onCreateExample={onCreateExample}
          onAddToTable={onAddToTable}
          availableTables={availableTables}
          lastSelectedTableId={lastSelectedTableId}
          onTableSelected={onTableSelected}
        />
      </div>
    );
  }

  // Current Mode Rendering (existing logic)
  const graphData = data as GraphData;

  if (!graphData.periods || graphData.periods.length === 0) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  const period = graphData.periods[0];

  // Handle backward compatibility: old configs had breakdown_by array
  let breakdownType = config.breakdown;
  if (!breakdownType && (config as any).breakdown_by) {
    breakdownType = (config as any).breakdown_by[0] || 'category';
  }

  const breakdownMeta = BREAKDOWN_METADATA[breakdownType || 'category'];

  // Transform breakdown data for charts
  const chartData = React.useMemo(() => {
    if (!period.breakdown) {
      return [];
    }

    const breakdownData = breakdownType === 'category'
      ? period.breakdown.by_category
      : period.breakdown.by_employees;

    if (!breakdownData || typeof breakdownData !== 'object') {
      return [];
    }

    // Transform the breakdown data into chart format
    const chartItems: { name: string; value: number }[] = [];

    if (breakdownType === 'category') {
      Object.entries(breakdownData).forEach(([catId, catData]: [string, any]) => {
        // Try to get the period-specific data
        const periodData = catData[period.period];

        if (periodData) {
          // Handle nested period structure
          const count = periodData.count || periodData.total || 0;
          if (count > 0) {
            chartItems.push({
              name: catData.category_name || catId,
              value: count
            });
          }
        } else if (typeof catData === 'object' && catData.category_name) {
          // Fallback: try to find count in any property that looks like a quarter
          const quarterKeys = Object.keys(catData).filter(key => /^\d{4}Q[1-4]$/.test(key));
          if (quarterKeys.length > 0) {
            const quarterData = catData[quarterKeys[0]];
            const count = quarterData?.count || quarterData?.total || 0;
            if (count > 0) {
              chartItems.push({
                name: catData.category_name || catId,
                value: count
              });
            }
          }
        }
      });
    } else if (breakdownType === 'employees') {
      Object.entries(breakdownData).forEach(([empRange, empData]: [string, any]) => {
        // Try to get the period-specific data
        const periodData = empData[period.period];

        if (periodData) {
          // Handle nested period structure
          const count = periodData.count || periodData.total || 0;
          if (count > 0) {
            chartItems.push({
              name: empRange,
              value: count
            });
          }
        } else if (typeof empData === 'object') {
          // Fallback: try to find count in any property that looks like a quarter
          const quarterKeys = Object.keys(empData).filter(key => /^\d{4}Q[1-4]$/.test(key));
          if (quarterKeys.length > 0) {
            const quarterData = empData[quarterKeys[0]];
            const count = quarterData?.count || quarterData?.total || 0;
            if (count > 0) {
              chartItems.push({
                name: empRange,
                value: count
              });
            }
          }
        }
      });
    }

    // Sort by value descending and take top 10
    return chartItems.sort((a, b) => b.value - a.value).slice(0, 10);
  }, [breakdownType, period]);

  // Calculate total
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Chart configuration for colors
  const chartConfig = {
    bar: {
      theme: {
        light: '#3b82f6',
        dark: '#3b82f6',
      },
    },
  };

  // Pie chart colors (Tailwind-inspired palette)
  const PIE_COLORS = [
    '#3b82f6', // blue-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#f59e0b', // amber-500
    '#10b981', // emerald-500
    '#06b6d4', // cyan-500
    '#f97316', // orange-500
    '#a855f7', // purple-500
    '#14b8a6', // teal-500
    '#6366f1', // indigo-500
  ];

  return (
    <div className="w-full space-y-2">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {config.title || breakdownMeta.label}
          {total > 0 && (
            <span className="ml-2 text-base font-normal text-gray-600">
              ({total.toLocaleString()} total)
            </span>
          )}
        </h3>
      </div>

      {/* Chart */}
      <div className={config.chartType === 'pie' ? 'h-[400px] pb-8' : 'h-[550px] pb-8'}>
        {config.chartType === 'bar' && chartData.length > 0 && (
          <ChartContainer config={chartConfig}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 30, bottom: 120 }}
            >
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{
                  value: 'Number of Companies',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#000000',
                }}
              />
              <Bar
                dataKey="value"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <ChartTooltip />
            </BarChart>
          </ChartContainer>
        )}

        {config.chartType === 'pie' && chartData.length > 0 && (
          <ChartContainer config={chartConfig}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <ChartTooltip />
              <Legend />
            </PieChart>
          </ChartContainer>
        )}

        {chartData.length === 0 && (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            <p>No data to display</p>
          </div>
        )}
      </div>
    </div>
  );
};
